import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TagBox from 'devextreme-react/tag-box';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import {
  customAppsAdminApi,
  CUSTOM_APP_CATEGORIES,
  type CustomAppItem,
  type CustomAppsAdminApi,
  type CustomAppUpsertRequest
} from './myAppsApi';
import { MyAppsIcon } from './MyAppsIcon';
import { useMyAppsStore } from './useMyAppsStore';
import './myApps.css';

const PAGE_SIZE = 50;

/**
 * DevExtreme's React wrapper diffs props by identity, so inline arrays, objects, and
 * render callbacks must be stable or every parent render re-pushes widget options.
 */
const NO_ROLES: readonly string[] = [];
const CATEGORY_OPTIONS = [...CUSTOM_APP_CATEGORIES];
const GRID_PAGING = { enabled: false };

function accessSummary(row: CustomAppItem): string {
  const targets = [...(row.allowedRoles ?? []), ...(row.allowedGroupIds ?? [])];
  return targets.length ? targets.join(', ') : 'Everyone';
}

function sameValues(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

type EditorState = {
  mode: 'create' | 'edit';
  id?: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  url: string;
  allowedRoles: string[];
  allowedGroupIdsText: string;
  isActive: boolean;
  hasIconImage: boolean;
  iconImageUrl: string | null;
  iconFile: File | null;
  clearIconImage: boolean;
};

const emptyEditor = (): EditorState => ({
  mode: 'create',
  slug: '',
  name: '',
  description: '',
  icon: 'dx-icon-globe',
  category: 'Microsoft 365',
  url: '',
  allowedRoles: [],
  allowedGroupIdsText: '',
  isActive: true,
  hasIconImage: false,
  iconImageUrl: null,
  iconFile: null,
  clearIconImage: false
});

export interface CustomAppsAdminPageProps {
  availableRoles?: readonly string[];
  /** Defaults to this site's own Custom Apps; pass a cross-site client to manage another site. */
  api?: CustomAppsAdminApi;
  title?: string;
  subtitle?: string;
  /**
   * Only the site that owns the apps should refresh its My Apps store after a save.
   * A host site administering another site has nothing local to reload.
   */
  refreshLocalMyApps?: boolean;
}

export function CustomAppsAdminPage({
  availableRoles = NO_ROLES,
  api = customAppsAdminApi,
  title = 'Custom Apps',
  subtitle,
  refreshLocalMyApps = true
}: CustomAppsAdminPageProps) {
  const [items, setItems] = useState<CustomAppItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState<string[]>([...CUSTOM_APP_CATEGORIES]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const refreshMyApps = useMyAppsStore((s) => s.load);

  useEffect(() => {
    if (!editor.iconFile) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(editor.iconFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editor.iconFile]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const reloadCategories = useCallback(async () => {
    try {
      const cats = await api.listCategories();
      const known = [...CUSTOM_APP_CATEGORIES];
      const extras = cats.map((c) => c.name).filter((name) => !known.includes(name as never));
      setCategories([...known, ...extras]);
    } catch {
      setCategories([...CUSTOM_APP_CATEGORIES]);
    }
  }, [api]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await api.list({
        q: debouncedQ || undefined,
        category: category === 'all' ? undefined : category,
        skip,
        take: PAGE_SIZE
      });
      setItems(page.items);
      setTotal(page.total);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load custom apps.');
    } finally {
      setLoading(false);
    }
  }, [api, category, debouncedQ, skip]);

  useEffect(() => {
    void reloadCategories();
  }, [reloadCategories]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const categoryChips = useMemo(() => ['all', ...categories], [categories]);
  // Keep saved roles selectable even after a role is dropped from the site role list.
  const roleOptions = useMemo(
    () => Array.from(new Set([...availableRoles, ...editor.allowedRoles])),
    [availableRoles, editor.allowedRoles]
  );

  function openCreate() {
    setEditor(emptyEditor());
    setEditorOpen(true);
  }

  const openEdit = useCallback((row: CustomAppItem) => {
    setEditor({
      mode: 'edit',
      id: row.id,
      slug: row.slug,
      name: row.name,
      description: row.description,
      icon: row.icon,
      category: row.category,
      url: row.url,
      allowedRoles: row.allowedRoles ?? [],
      allowedGroupIdsText: (row.allowedGroupIds ?? []).join(', '),
      isActive: row.isActive,
      hasIconImage: row.hasIconImage,
      iconImageUrl: api.iconUrl(row),
      iconFile: null,
      clearIconImage: false
    });
    setEditorOpen(true);
  }, [api]);

  const previewIcon =
    filePreviewUrl ||
    (!editor.clearIconImage && editor.iconImageUrl ? editor.iconImageUrl : editor.icon);

  async function saveEditor() {
    const body: CustomAppUpsertRequest = {
      slug: editor.slug.trim(),
      name: editor.name.trim(),
      description: editor.description.trim(),
      icon: editor.icon.trim(),
      category: editor.category.trim(),
      url: editor.url.trim(),
      allowedRoles: editor.allowedRoles,
      allowedGroupIds: editor.allowedGroupIdsText
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean),
      isActive: editor.isActive
    };
    const hasImage = Boolean(editor.iconFile) || (editor.hasIconImage && !editor.clearIconImage);
    if (!body.slug || !body.name || !body.category || !body.url) {
      showErrorToast('Slug, name, category, and URL are required.');
      return;
    }
    if (!body.icon && !hasImage) {
      showErrorToast('Add an emoji, DevExtreme icon, image URL, or a PNG/ICO file.');
      return;
    }

    setBusy(true);
    try {
      const saved =
        editor.mode === 'create'
          ? await api.create(body)
          : editor.id
            ? await api.update(editor.id, body)
            : null;
      if (!saved) throw new Error('Could not save custom app.');
      if (editor.iconFile) await api.uploadIcon(saved.id, editor.iconFile);
      else if (editor.clearIconImage && saved.hasIconImage) await api.clearIcon(saved.id);
      showSuccessToast(editor.mode === 'create' ? 'Custom app added.' : 'Custom app updated.');
      setEditorOpen(false);
      await reloadCategories();
      await reload();
      if (refreshLocalMyApps) await refreshMyApps();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save custom app.');
    } finally {
      setBusy(false);
    }
  }

  const onDeactivate = useCallback(
    async (row: CustomAppItem) => {
      setBusy(true);
      try {
        await api.deactivate(row.id);
        showSuccessToast('Custom app deactivated.');
        await reload();
        if (refreshLocalMyApps) await refreshMyApps();
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Could not deactivate custom app.');
      } finally {
        setBusy(false);
      }
    },
    [api, refreshLocalMyApps, refreshMyApps, reload]
  );

  const renderIconCell = useCallback(
    (cell: { data?: unknown }) => {
      const row = cell.data as CustomAppItem;
      return (
        <span className="custom-apps-admin__icon">
          <MyAppsIcon icon={api.iconUrl(row) || row.icon} />
        </span>
      );
    },
    [api]
  );

  const renderActionsCell = useCallback(
    (cell: { data?: unknown }) => {
      const row = cell.data as CustomAppItem;
      return (
        <div className="custom-apps-admin__actions">
          <Button text="Edit" stylingMode="text" onClick={() => openEdit(row)} />
          {row.isActive ? (
            <Button
              text="Deactivate"
              stylingMode="text"
              type="danger"
              onClick={() => void onDeactivate(row)}
            />
          ) : null}
        </div>
      );
    },
    [onDeactivate, openEdit]
  );

  const onRowDblClick = useCallback(
    (e: { data?: unknown }) => {
      if (e.data) openEdit(e.data as CustomAppItem);
    },
    [openEdit]
  );

  const pageIndex = Math.floor(skip / PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="ops-fill-page custom-apps-admin">
      <header className="ops-fill-page__header custom-apps-admin__header">
        <div>
          <h2 className="dashboard-title">{title}</h2>
          <p className="dashboard-subtitle custom-apps-admin__muted">
            {subtitle ??
              'Active apps appear in My Apps and the sidebar immediately. Opening a card launches the link in a new tab. Visibility can be limited by app role or Entra group.'}
          </p>
        </div>
        <div className="custom-apps-admin__toolbar">
          {loading || busy ? <LoadIndicator visible height={18} width={18} /> : null}
          <Button text="Add item" type="default" icon="plus" onClick={openCreate} />
          <Button
            text="Refresh"
            icon="refresh"
            stylingMode="outlined"
            onClick={() => void reload()}
          />
        </div>
      </header>

      <div className="ops-fill-page__panel custom-apps-admin__panel">
        <div className="custom-apps-admin__filters">
          <div className="custom-apps-admin__chips" role="group" aria-label="Filter by category">
            {categoryChips.map((name) => (
              <Button
                key={name}
                text={name === 'all' ? 'All' : name}
                stylingMode={category === name ? 'contained' : 'outlined'}
                type={category === name ? 'default' : 'normal'}
                onClick={() => {
                  setSkip(0);
                  setCategory(name);
                }}
              />
            ))}
          </div>
          <TextBox
            value={q}
            width="100%"
            mode="search"
            placeholder="Search name, slug, or URL…"
            showClearButton
            onValueChanged={(e) => {
              setSkip(0);
              setQ(String(e.value ?? ''));
            }}
          />
          <div className="custom-apps-admin__pager">
            <span className="custom-apps-admin__muted">
              Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
            </span>
            <div className="custom-apps-admin__pager-actions">
              <Button
                text="Previous"
                stylingMode="outlined"
                disabled={pageIndex <= 0 || loading}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              />
              <span className="custom-apps-admin__muted">
                Page {pageIndex + 1} / {pageCount}
              </span>
              <Button
                text="Next"
                stylingMode="outlined"
                disabled={pageIndex + 1 >= pageCount || loading}
                onClick={() => setSkip(skip + PAGE_SIZE)}
              />
            </div>
          </div>
        </div>

        <div className="ops-fill-page__grid-host">
          <DataGrid
            dataSource={items}
            keyExpr="id"
            showBorders
            rowAlternationEnabled
            hoverStateEnabled
            height="100%"
            columnAutoWidth
            wordWrapEnabled
            paging={GRID_PAGING}
            onRowDblClick={onRowDblClick}
          >
            <Scrolling mode="standard" showScrollbar="always" useNative />
            <SearchPanel visible={false} />
            <Column caption="Icon" width={80} allowSorting={false} cellRender={renderIconCell} />
            <Column dataField="name" caption="Name" minWidth={160} />
            <Column dataField="slug" caption="Id" width={140} />
            <Column dataField="category" caption="Category" width={140} />
            <Column caption="Access" minWidth={180} calculateCellValue={accessSummary} />
            <Column dataField="url" caption="URL" minWidth={220} />
            <Column dataField="isActive" caption="Active" width={80} dataType="boolean" />
            <Column
              caption="Actions"
              width={180}
              allowSorting={false}
              cellRender={renderActionsCell}
            />
          </DataGrid>
        </div>
      </div>

      <Popup
        visible={editorOpen}
        onHiding={() => setEditorOpen(false)}
        dragEnabled
        showCloseButton
        title={editor.mode === 'create' ? 'Add custom app' : 'Edit custom app'}
        width="min(720px, calc(100vw - 32px))"
        height="min(820px, calc(100vh - 32px))"
      >
        <div className="custom-apps-admin__editor">
          <div className="custom-apps-admin__preview" aria-label="Card preview">
            <div className="custom-apps-admin__preview-icon">
              <MyAppsIcon icon={previewIcon || 'dx-icon-globe'} />
            </div>
            <div className="custom-apps-admin__preview-copy">
              <span className="custom-apps-admin__preview-label">My Apps preview</span>
              <strong>{editor.name || 'App name'}</strong>
              <span className="custom-apps-admin__muted">{editor.category}</span>
            </div>
          </div>

          <section className="custom-apps-admin__form-section">
            <div className="custom-apps-admin__section-heading">
              <strong>App details</strong>
              <span>Name, destination, and catalog placement.</span>
            </div>
            <div className="custom-apps-admin__form-grid">
              <TextBox
                value={editor.name}
                label="Name"
                stylingMode="outlined"
                onValueChanged={(e) =>
                  setEditor((prev) => ({ ...prev, name: String(e.value ?? '') }))
                }
              />
              <TextBox
                value={editor.slug}
                label="Id (slug)"
                stylingMode="outlined"
                onValueChanged={(e) =>
                  setEditor((prev) => ({ ...prev, slug: String(e.value ?? '') }))
                }
              />
              <div className="custom-apps-admin__field--wide">
                <TextBox
                  value={editor.description}
                  label="Description"
                  stylingMode="outlined"
                  onValueChanged={(e) =>
                    setEditor((prev) => ({ ...prev, description: String(e.value ?? '') }))
                  }
                />
              </div>
              <SelectBox
                value={editor.category}
                label="Category"
                stylingMode="outlined"
                dataSource={CATEGORY_OPTIONS}
                onValueChanged={(e) =>
                  setEditor((prev) => ({
                    ...prev,
                    category: String(e.value ?? 'Microsoft 365')
                  }))
                }
              />
              <TextBox
                value={editor.url}
                label="Destination URL"
                stylingMode="outlined"
                onValueChanged={(e) =>
                  setEditor((prev) => ({ ...prev, url: String(e.value ?? '') }))
                }
              />
            </div>
            <p className="custom-apps-admin__hint">
              Any secure http(s) URL works, including Microsoft 365 launch and My Apps links.
            </p>
          </section>

          <section className="custom-apps-admin__form-section">
            <div className="custom-apps-admin__section-heading">
              <strong>Icon</strong>
              <span>Use an uploaded brand icon, URL, emoji, or DevExtreme icon class.</span>
            </div>
            <TextBox
              value={editor.icon}
              label="Icon URL, emoji, or class"
              stylingMode="outlined"
              onValueChanged={(e) =>
                setEditor((prev) => ({ ...prev, icon: String(e.value ?? '') }))
              }
            />
            <div className="custom-apps-admin__file">
              <label className="custom-apps-admin__file-picker">
                <span>Upload PNG or ICO</span>
                <input
                  aria-label="PNG or ICO file"
                  type="file"
                  accept=".png,.ico,image/png,image/x-icon,image/vnd.microsoft.icon"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setEditor((prev) => ({ ...prev, iconFile: file, clearIconImage: false }));
                  }}
                />
              </label>
              <span className="custom-apps-admin__file-name">
                {editor.iconFile?.name ??
                  (editor.hasIconImage && !editor.clearIconImage
                    ? 'Uploaded icon in use'
                    : 'No uploaded icon')}
              </span>
              {editor.iconFile || (editor.hasIconImage && !editor.clearIconImage) ? (
                <Button
                  text="Remove"
                  stylingMode="text"
                  type="danger"
                  onClick={() =>
                    setEditor((prev) => ({ ...prev, iconFile: null, clearIconImage: true }))
                  }
                />
              ) : null}
            </div>
            <p className="custom-apps-admin__hint">
              PNG or ICO, 128 KB maximum. An uploaded file takes priority over the icon field.
            </p>
          </section>

          <section className="custom-apps-admin__form-section">
            <div className="custom-apps-admin__section-heading">
              <strong>Access</strong>
              <span>Leave both fields empty to make this app available to everyone.</span>
            </div>
            <div className="custom-apps-admin__access-grid">
              <TagBox
                value={editor.allowedRoles}
                label="Allowed app roles"
                stylingMode="outlined"
                dataSource={roleOptions}
                showSelectionControls
                searchEnabled
                onValueChanged={(e) =>
                  setEditor((prev) => {
                    const next = Array.isArray(e.value) ? e.value.map(String) : [];
                    return sameValues(prev.allowedRoles, next)
                      ? prev
                      : { ...prev, allowedRoles: next };
                  })
                }
              />
              <TextBox
                value={editor.allowedGroupIdsText}
                label="Allowed Entra group object IDs"
                stylingMode="outlined"
                onValueChanged={(e) =>
                  setEditor((prev) => ({
                    ...prev,
                    allowedGroupIdsText: String(e.value ?? '')
                  }))
                }
              />
            </div>
            <p className="custom-apps-admin__hint">
              A matching role or group grants access. Separate multiple group IDs with commas.
            </p>
          </section>

          <div className="custom-apps-admin__editor-actions">
            <CheckBox
              value={editor.isActive}
              text="Active — show in My Apps"
              onValueChanged={(e) => {
                if (!e.event) return;
                setEditor((prev) => ({ ...prev, isActive: Boolean(e.value) }));
              }}
            />
            <div className="custom-apps-admin__editor-buttons">
              <Button text="Cancel" stylingMode="outlined" onClick={() => setEditorOpen(false)} />
              <Button
                text={busy ? 'Saving…' : 'Save app'}
                type="default"
                disabled={busy}
                onClick={() => void saveEditor()}
              />
            </div>
          </div>
        </div>
      </Popup>
    </div>
  );
}
